import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { transform } from 'ol/proj';
import VectorSource from "ol/source/Vector";
import { Style, RegularShape, Stroke } from 'ol/style';
import type { MetadataObject } from '../types';

// Style cache for GBIF points
const gbifStyleCache = new globalThis.Map<string, Style>();

/**
 * Gets a style for GBIF data points - using triangles with color based on species group
 * @param speciesGroup - The species group to determine color
 * @param zoom - Current zoom level to determine size
 */
export const getGbifStyle = (speciesGroup: string, zoom: number): Style => {
  const cacheKey = `gbif-${speciesGroup}`;

  // Check if we have a cached style
  const cachedStyle = gbifStyleCache.get(cacheKey);
  if (cachedStyle) {
    const oldShape = cachedStyle.getImage() as RegularShape;

    // Create a new RegularShape with the updated radius
    const newShape = new RegularShape({
      stroke: oldShape.getStroke() || undefined,
      points: 4,
      radius: Math.max(6, zoom),
      radius2: 0,
      angle: Math.PI / 4,
    });

    // Update the style with the new shape
    cachedStyle.setImage(newShape);

    return cachedStyle;
  }

  // Adjust the radius based on zoom level
  const style = new Style({
    // X Shape point
    image: new RegularShape({
      stroke: new Stroke({ color: 'rgba(2, 2, 2, 0.5)', width: 2, lineCap: 'round', }),
      points: 4,
      radius: Math.max(6, zoom),
      radius2: 0,
      angle: Math.PI / 4,
    }),
  });

  gbifStyleCache.set(cacheKey, style);

  return style;
};

/**
 * Enhanced helper function to find species group with multiple fallback strategies
 * @param scientificName - The scientific name to look up
 * @param groupMap - Map of scientific names to species groups
 */
export const findSpeciesGroup = (scientificName: string, groupMap: Map<string, string>): string => {
  // Edge case handling
  if (!scientificName) return 'gbif_default';

  // Ensure groupMap is actually a Map
  if (!groupMap || typeof groupMap.get !== 'function') {
    return 'gbif_default';
  }

  // Strategy 1: Try exact match first
  let group = groupMap.get(scientificName);
  if (group) return group;

  // Strategy 2: Try without author information (removes text in parentheses or after first uppercase letter following space)
  const simplifiedName = scientificName.replace(/\s+\(.+\)|\s+[A-Z].+/, '').trim();
  group = groupMap.get(simplifiedName);
  if (group) return group;

  // Strategy 3: Try to match by first two parts of the name (genus + species, without subspecies)
  const nameParts = scientificName.split(/\s+/);

  if (nameParts?.length >= 2) {
    const genusSpecies = `${nameParts[0]} ${nameParts[1]}`.trim();
    group = groupMap.get(genusSpecies);
    if (group) return group;

    // Check if any key in the map starts with this genus-species combination
    // Make sure groupMap is iterable before trying to use entries()
    if (typeof groupMap.entries === 'function') {
      try {
        for (const [key, value] of groupMap.entries()) {
          if (key.startsWith(genusSpecies)) {
            return value;
          }
        }
      } catch (err) {
        console.log('Error iterating groupMap:', err);
        // Continue with other strategies
      }
    }
  }

  // Strategy 4: Try to match by genus only - with safe iteration
  if (nameParts?.length >= 1 && typeof groupMap.entries === 'function') {
    const genus = nameParts[0]?.trim() || '';

    try {
      // Find any species in the same genus
      for (const [key, value] of groupMap.entries()) {
        if (key.startsWith(genus + ' ')) {
          return value;
        }
      }
    } catch (err) {
      console.log('Error iterating groupMap for genus matching:', err);
      // Continue with other strategies
    }
  }

  // Strategy 5: Case insensitive matching - with safe iteration
  if (typeof groupMap.entries === 'function') {
    const lowerScientificName = scientificName.toLowerCase();
    try {
      for (const [key, value] of groupMap.entries()) {
        if (key.toLowerCase() === lowerScientificName) {
          return value;
        }
      }
    } catch (err) {
      console.log('Error iterating groupMap for case-insensitive matching:', err);
      // Continue with other strategies
    }
  }

  // Strategy 6: Check for common taxonomic indicators in the name
  const lowerName = scientificName.toLowerCase();

  if (/\b(carex|salix|quercus|fagus|abies|picea|pinus|populus|acer|betula|alnus|arabidopsis)\b/.test(lowerName)) {
    return 'plant';
  } else if (/\b(canis|vulpes|ursus|cervus|lepus|sciurus|mus|rattus|myotis|chiroptera)\b/.test(lowerName)) {
    return 'vertebrate';
  } else if (/\b(apis|bombus|papilio|formica|araneus|ixodes|gastropoda|mollusca)\b/.test(lowerName)) {
    return 'invertebrate';
  } else if (/\b(amoeba|paramecium|euglena|phytoplankton|diatom|algae)\b/.test(lowerName)) {
    return 'protista';
  } else if (/\b(agaricus|boletus|amanita|morchella|penicillium|aspergillus)\b/.test(lowerName)) {
    return 'fungi';
  }

  // For GBIF data points without a match, return a special identifier for default GBIF coloring
  return 'gbif_default';
};

/**
 * Extract unique species names from metadata or filters
 * @param filteredMetadata - The filtered metadata to extract from
 * @param speciesScientificFilter - Optional direct species filter
 */
export const getUniqueSpeciesNames = (
  filteredMetadata: MetadataObject[],
  speciesScientificFilter?: any
): Set<string> => {
  const speciesNames = new Set<string>();

  if (speciesScientificFilter) {
    // Use direct species filter if available
    speciesScientificFilter.forEach((name: string) => {
      if (name) speciesNames.add(name);
    });
  } else {
    // Otherwise, extract species from filtered metadata
    filteredMetadata.forEach(metadata => {
      if (metadata.species_scientific) {
        const species = metadata.species_scientific.split(';').map((s: string) => s.trim());
        species.forEach((s: string) => {
          if (s) speciesNames.add(s);
        });
      }
    });
  }

  return speciesNames;
};

/**
 * Create mapping between species names and their groups
 * @param filteredMetadata - The filtered metadata to create mapping from
 */
export const createSpeciesGroupMap = (filteredMetadata: MetadataObject[]): Map<string, string> => {
  const speciesGroupMap = new Map<string, string>();

  filteredMetadata.forEach(metadata => {
    if (metadata.species_scientific && metadata.species_group) {
      const species = metadata.species_scientific.split(';').map((s: string) => s.trim());
      species.forEach((s: string) => {
        if (s && metadata.species_group) speciesGroupMap.set(s, metadata.species_group);
      });
    }
  });

  return speciesGroupMap;
};

/**
 * Process an occurrence to determine if it should be included
 * @param occurrence - The GBIF occurrence to process
 * @param speciesName - The species name to match against
 */
export const processOccurrence = (occurrence: any, speciesName: string): boolean => {
  // Skip records without coordinates
  if (!occurrence.decimalLatitude || !occurrence.decimalLongitude) return false;

  const occurrenceScientificName = occurrence.scientificName || '';

  // Get base names for comparison (just genus + species)
  const queryParts = speciesName.toLowerCase().split(' ');
  const occParts = occurrenceScientificName.toLowerCase().split(' ');

  const queryBaseName = queryParts.length >= 2
    ? `${queryParts[0]} ${queryParts[1]}`
    : speciesName.toLowerCase();

  const resultBaseName = occParts.length >= 2
    ? `${occParts[0]} ${occParts[1]}`
    : occurrenceScientificName.toLowerCase();

  // Check if names match
  const baseNameMatch = queryBaseName === resultBaseName;

  // Special case: Block Cardaminopsis when searching for Arabidopsis
  const isCardaminopsis = occurrenceScientificName.toLowerCase().includes('cardaminopsis');
  const isArabidopsisQuery = speciesName.toLowerCase().includes('arabidopsis');
  const shouldBlock = isCardaminopsis && isArabidopsisQuery;

  return baseNameMatch && !shouldBlock;
};

/**
 * Add a feature to the map for a GBIF occurrence
 * @param occurrence - The GBIF occurrence data
 * @param speciesName - The species name used for filtering
 * @param speciesGroupMap - Map of species names to groups
 * @param gbifVectorSource - The vector source to add features to
 * @param mapProjection - The map projection (e.g., 'EPSG:21781')
 * @param zoom - Current map zoom level
 */
export const addFeatureToMap = (
  occurrence: any,
  speciesName: string,
  speciesGroupMap: Map<string, string>,
  gbifVectorSource: VectorSource,
  mapProjection: string,
  zoom: number
): void => {
  const originalCoords = [occurrence.decimalLongitude, occurrence.decimalLatitude];

  // Transform to map projection
  const coordinates = transform(
    originalCoords as [number, number],
    'EPSG:4326',
    mapProjection
  );

  const point = new Point(coordinates);
  const feature = new Feature({
    geometry: point,
    gbif_id: occurrence.gbifID,
    scientific_name: occurrence.scientificName,
    filter_name: speciesName,
    species_group: findSpeciesGroup(occurrence.scientificName, speciesGroupMap),
    dataset_key: occurrence.datasetKey,
    country: occurrence.country,
    year: occurrence.year,
    month: occurrence.month,
    day: occurrence.day,
    basis_of_record: occurrence.basisOfRecord,
    original_coords: originalCoords
  });

  // Set style for the feature
  feature.setStyle(getGbifStyle(findSpeciesGroup(occurrence.scientificName, speciesGroupMap), zoom));

  // Add to vector source
  gbifVectorSource.addFeature(feature);
};

/**
 * Fetch data for a single species from GBIF API
 * @param speciesName - The species name to search for
 * @param speciesGroupMap - Map of species names to groups
 * @param gbifVectorSource - The vector source to add features to
 * @param mapProjection - The map projection (e.g., 'EPSG:21781')
 * @param zoom - Current map zoom level
 * @param onlySwissData - Whether to restrict results to Switzerland
 */
export const fetchSpeciesData = async (
  speciesName: string,
  speciesGroupMap: Map<string, string>,
  gbifVectorSource: VectorSource,
  mapProjection: string,
  zoom: number
): Promise<void> => {
  try {
    // Extract just genus+species for search (first two terms)
    const nameParts = speciesName.toLowerCase().split(' ');
    const searchTerm = nameParts.length >= 2
      ? `${nameParts[0]} ${nameParts[1]}`
      : speciesName;

    // First endpoint: Precise scientific name search (taxonomic backbone search)
    const preciseUrl = `https://api.gbif.org/v1/occurrence/search?scientificName=${encodeURIComponent(searchTerm)}&limit=20000&country=CH`;

    // Second endpoint: More flexible search (full text search across fields)
    const generalUrl = `https://api.gbif.org/v1/occurrence/search?q=${encodeURIComponent(searchTerm)}&limit=20000&country=CH`;

    // Use the general URL first, we'll fetch from both
    const apiUrl = generalUrl;
/*
    console.log(`Using hybrid search approach for ${speciesName}`);
    console.log(`URL 1 (precise): ${preciseUrl}`);
    console.log(`URL 2 (general): ${generalUrl}`);

    console.log(`Searching GBIF with URL: ${apiUrl}`);
*/

    const response = await fetch(apiUrl);
    const data = await response.json();
/*
    console.log(`Received ${data.results?.length || 0} GBIF records for ${speciesName} from general search`);

    // Now get the precise search results too
    console.log(`Fetching additional records with precise search...`);
*/
    const preciseResponse = await fetch(preciseUrl);
    const preciseData = await preciseResponse.json();
//    console.log(`Received ${preciseData.results?.length || 0} GBIF records from precise search`);

    // Create a set of existing IDs to avoid duplicates
    const existingIds = new Set();
    if (data.results) {
      data.results.forEach((r: any) => {
        existingIds.add(r.key || r.gbifID);
      });
    }

    // Filter out any duplicates
    const uniqueNewRecords = preciseData.results ? preciseData.results.filter((r: any) => {
      const id = r.key || r.gbifID;
      return !existingIds.has(id);
    }) : [];

//    console.log(`Found ${uniqueNewRecords.length} unique additional records from precise search`);

    // Add the unique records to our combined results
    if (data.results) {
      data.results = [...data.results, ...uniqueNewRecords];
//      console.log(`Combined total: ${data.results.length} records`);
    } else {
      data.results = uniqueNewRecords;
//      console.log(`Using only precise search results: ${data.results.length} records`);
    }

    if (!data.results || data.results.length === 0) {
//      console.log(`No GBIF data found for species: ${speciesName}`);
      return;
    }

    // Process matching occurrences
    data.results.forEach((occurrence: any) => {
      if (processOccurrence(occurrence, speciesName)) {
        addFeatureToMap(occurrence, speciesName, speciesGroupMap, gbifVectorSource, mapProjection, zoom);
      }
    });

  } catch (error) {
    console.error(`Error fetching GBIF data for ${speciesName}:`, error);
  }
};

/**
 * Main function to fetch GBIF data based on species filters
 * @param filteredMetadata - The filtered metadata to extract species from
 * @param gbifVectorSource - The vector source to add features to
 * @param speciesGroupFilter - Optional species group filter
 * @param speciesScientificFilter - Optional species scientific name filter
 * @param mapProjection - The map projection (e.g., 'EPSG:21781')
 * @param zoom - Current map zoom level
 * @param onlySwissData - Whether to restrict results to Switzerland
 */
export const fetchGbifData = async (
  filteredMetadata: MetadataObject[],
  gbifVectorSource: VectorSource,
  speciesGroupFilter: any,
  speciesScientificFilter: any,
  mapProjection: string,
  zoom: number
): Promise<void> => {
  // Clear previous GBIF data
  gbifVectorSource.clear();

  // If no filtered metadata, don't show any GBIF data
  if (!filteredMetadata || filteredMetadata.length === 0) {
    console.log('No filtered metadata available, not fetching GBIF data');
    return;
  }

  if (!speciesGroupFilter && !speciesScientificFilter) {
    console.log('No species filters active, not fetching GBIF data');
    return;
  }

  // Extract unique species names from filters or metadata
  const speciesNames = getUniqueSpeciesNames(filteredMetadata, speciesScientificFilter);

  if (speciesNames.size === 0) {
    console.log('No species names found after filtering, not fetching GBIF data');
    return;
  }

  // console.log('Fetching GBIF data for species:', Array.from(speciesNames));

  // Create a map to store species_scientific to species_group mapping
  const speciesGroupMap = createSpeciesGroupMap(filteredMetadata);

  // Fetch data for each species from GBIF API in parallel
  const fetchPromises = Array.from(speciesNames).map(speciesName =>
    fetchSpeciesData(
      speciesName,
      speciesGroupMap,
      gbifVectorSource,
      mapProjection,
      zoom
    )
  );

  // Wait for all fetch operations to complete
  await Promise.all(fetchPromises);
  console.log(`Added ${gbifVectorSource.getFeatures().length} GBIF features to the map`);
};
